import React, { useEffect, useState } from 'react';
import { Modal, Typography, Spin } from 'antd';
import { IconMapPin } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import StaticMap from './StaticMap';
import { getHeavyDutyMachineryById } from '../services/HeavyDutyMachineryService';
import { IHeavyDutyMachinery } from '../interfaces/heavyDutyMachinery/IHeavyDutyMachinery';
import { IBooking } from '../interfaces/heavyDutyMachinery/IBooking';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { buildBookingLocationView } from '../helpers/bookingLocationHelper';
import { useAuth } from '../contextProviders/AuthProvider';

const { Text } = Typography;

interface AddressMapModalProps {
  booking: IBooking;
  open: boolean;
  onClose: () => void;
}

/**
 * "Ver dirección" popup for the /messages thread -- the same location
 * BookingDetailsPanel shows, at a size somebody can actually read off a
 * phone before driving there.
 *
 * Both read it through bookingLocationHelper rather than each testing
 * `booking.privateAddress` for themselves: they used to, identically and
 * identically wrongly, and a fix applied to one of them would not have
 * reached the other. The map itself is the space's public pin -- shown
 * whatever the status, because it is the same pin every listing page shows
 * and hiding it protects nobody.
 */
const AddressMapModal: React.FC<AddressMapModalProps> = ({ booking, open, onClose }) => {
  const { t } = useTranslation();
  const { userId } = useAuth();
  const [space, setSpace] = useState<IHeavyDutyMachinery | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const location = buildBookingLocationView(booking, !!userId && booking.guestUserId !== userId);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getHeavyDutyMachineryById(booking.heavyDutyMachineryId)
      .then(setSpace)
      .finally(() => setLoading(false));
  }, [open, booking.heavyDutyMachineryId]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_ADDRESS_MODAL_TITLE)}
      styles={{
        content: {
          borderRadius: 20,
          border: '0.5px solid rgba(0,0,0,0.08)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        },
      }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : (
        <>
          {space?.latitude != null && space?.longitude != null && (
            <StaticMap
              latitude={space.latitude}
              longitude={space.longitude}
              width={600}
              height={260}
              style={{ width: '100%', height: 'auto', maxWidth: '100%' }}
            />
          )}
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
            <IconMapPin size={16} style={{ marginTop: 2, flexShrink: 0 }} />
            <div>
              <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, display: 'block' }}>
                {translate(t, location.pickupLabelKey)}
              </Text>
              {location.pickupAddress ? (
                <Text>{location.pickupAddress}</Text>
              ) : (
                <Text type="secondary" style={{ fontStyle: 'italic' }}>
                  {translate(t, location.pickupMissingKey!)}
                </Text>
              )}
            </div>
          </div>

          {/* Where it is GOING, on a delivered booking. The popup used to
              name only the yard, which on half of these bookings is the one
              address neither party has to travel to. */}
          {location.delivered && location.deliveryPoint && (
            <div style={{ marginTop: 16 }}>
              <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 6 }}>
                {translate(t, location.deliveryLabelKey)}
              </Text>
              <StaticMap
                latitude={location.deliveryPoint.latitude}
                longitude={location.deliveryPoint.longitude}
                width={600}
                height={200}
                style={{ width: '100%', height: 'auto', maxWidth: '100%' }}
              />
              {location.deliveryInstructions && <Text style={{ display: 'block', marginTop: 8 }}>{location.deliveryInstructions}</Text>}
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default AddressMapModal;
